// 原生攝影機的預置點（名稱 + 縮圖；plan SPEC #multi-lens-ptz、#multi-lens-presets，單鏡頭雲台機種同一套）：
// 平台 /v1/homes/{home}/things/{device}/ptz/presets，以設備清單為主——平台有資料的用名稱與縮圖、沒有的寫「位置 n」、選單多〔命名〕。
// 點卡片 = kinio_ptz.goto_preset（到位才回 ack，最長 20 秒），成功亮「目前所在」；被新的 goto / jog 取代（preempted）丟掉不提示。
// 新增：名稱預填「位置 n」→ POST（平台挑號、設備存目前位置）→ 有縮圖來源（畫面正在看 pan_tilt 那顆）就 PUT 縮圖；
// 改名 / 命名只動平台（設備離線也行）、更新位置與刪除要設備。409 VERSION_CONFLICT → 重拉並提示。面板開著每 30 秒重拉。
// 通道（NVR / Station 底下的 ONVIF）照舊讀設備名稱（PtzPanel）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KinioError,
  PresetHere,
  cleanPresetName,
  createPtzPreset,
  deletePtzPreset,
  gotoPtzPreset,
  kinioFetchResponse,
  listPtzPresets,
  planeBase,
  presetDefaultNameNumber,
  presetErrorOf,
  putPtzPresetThumbnail,
  renamePtzPreset,
  updatePtzPresetPosition,
  type PtzPreset,
  type PtzPresetList,
} from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';

const POLL_MS = 30_000;

/** 縮圖：平台的 thumb_url（相對 plane 的路徑或完整網址）帶 Bearer 抓成 blob URL；失敗 = 不畫縮圖。 */
function useThumb(api: string, url: string | null): string | null {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!url) {
      setSrc(null);
      return;
    }
    let alive = true;
    let obj: string | null = null;
    const full = /^https?:\/\//.test(url) ? url : planeBase(api) + url;
    kinioFetchResponse(full)
      .then((r) => r.blob())
      .then((b) => {
        if (!alive) return;
        obj = URL.createObjectURL(b);
        setSrc(obj);
      })
      .catch(() => alive && setSrc(null));
    return () => {
      alive = false;
      if (obj) URL.revokeObjectURL(obj);
    };
  }, [api, url]);
  return src;
}

function PresetCard({
  cam,
  p,
  here,
  going,
  manage,
  onGoto,
  onMenu,
}: {
  cam: VmsDevice;
  p: PtzPreset;
  here: boolean;
  going: boolean;
  manage: boolean;
  onGoto(): void;
  onMenu(): void;
}) {
  const { t } = useTranslation();
  const thumb = useThumb(cam.apiEndpoint, p.thumbUrl);
  const name = p.name ?? t('ptz.preset_n', { n: p.id });
  return (
    <div className={`ptz-preset-card${here ? ' here' : ''}${going ? ' going' : ''}`} data-preset={p.id}>
      <button
        type="button"
        className="ptz-preset-hit"
        aria-label={t(p.named ? 'ptz.preset_card_a11y' : 'ptz.preset_card_unnamed_a11y', { name })}
        aria-description={here ? t('ptz.preset_here') : going ? t('ptz.preset_going', { name }) : undefined}
        onClick={manage ? onMenu : onGoto}
        onContextMenu={(e) => {
          e.preventDefault();
          onMenu();
        }}
      >
        <span className="ptz-preset-thumb" aria-hidden="true">
          {thumb ? <img src={thumb} alt="" /> : null}
          {going && <span className="ptz-preset-ring" />}
        </span>
        <span className="ptz-preset-name">
          {here ? '✓ ' : ''}
          {name}
        </span>
      </button>
      <button type="button" className="icon-btn ptz-preset-more" aria-label={t('ptz.preset_menu')} aria-haspopup="menu" onClick={onMenu}>
        ⋯
      </button>
    </div>
  );
}

export function DevicePresets({
  cam,
  captureThumb,
  onBeforeMove,
}: {
  cam: VmsDevice;
  /** 存預置點時的縮圖（320×180 JPEG）；畫面不在 pan_tilt 那顆 / 抓不到 = null（先存無縮圖）。 */
  captureThumb?: () => Promise<Blob | null>;
  /** goto 前通知面板（停掉按住中的 jog）。 */
  onBeforeMove?: () => void;
}) {
  const { t } = useTranslation();
  const [list, setList] = useState<PtzPresetList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [going, setGoing] = useState<number | null>(null);
  const [manage, setManage] = useState(false);
  const [menu, setMenu] = useState<PtzPreset | null>(null);
  const [form, setForm] = useState<{ mode: 'add' | 'rename'; preset?: PtzPreset; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const here = useRef(new PresetHere());
  const [, bump] = useState(0);
  const gotoSeq = useRef(0);

  const reload = useCallback(async () => {
    try {
      setList(await listPtzPresets(cam.apiEndpoint, cam.homeId, cam.aid));
    } catch (e) {
      // 讀不到預置點不擋方向盤：只是不畫這一排
      if (!(e instanceof KinioError && e.status === 404)) setErr(t('ptz.preset_err_OTHER'));
    }
  }, [cam.apiEndpoint, cam.homeId, cam.aid, t]);

  useEffect(() => {
    void reload();
    const iv = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(iv);
  }, [reload]);

  const errText = (e: unknown, name: string) => {
    const k = presetErrorOf(e);
    if (k === 'CONFLICT') {
      void reload();
      return t('ptz.preset_conflict');
    }
    return t(`ptz.preset_err_${k === 'PREEMPTED' ? 'OTHER' : k}`, { name });
  };

  const goto = async (p: PtzPreset) => {
    const name = p.name ?? t('ptz.preset_n', { n: p.id });
    onBeforeMove?.();
    here.current.moved();
    const seq = ++gotoSeq.current;
    setGoing(p.id);
    setErr(null);
    setNote(t('ptz.preset_going', { name }));
    try {
      await gotoPtzPreset(cam.apiEndpoint, cam.homeId, cam.aid, p.id);
      if (seq !== gotoSeq.current) return; // 被新的 goto 取代
      here.current.gotoAcked(p.id, Date.now());
      setNote(t('ptz.preset_arrived', { name }));
    } catch (e) {
      if (seq !== gotoSeq.current || presetErrorOf(e) === 'PREEMPTED') return; // 取代：丟掉、不提示
      setNote(null);
      setErr(errText(e, name));
    } finally {
      if (seq === gotoSeq.current) setGoing(null);
      bump((x) => x + 1);
    }
  };

  const save = async () => {
    if (!form || busy) return;
    const name = cleanPresetName(form.name);
    if (!name) {
      setErr(t('ptz.preset_err_INVALID_NAME'));
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (form.mode === 'add') {
        const created = await createPtzPreset(cam.apiEndpoint, cam.homeId, cam.aid, name);
        const jpeg = await captureThumb?.().catch(() => null);
        if (jpeg && created.id) await putPtzPresetThumbnail(cam.apiEndpoint, cam.homeId, cam.aid, created.id, jpeg).catch(() => undefined); // 縮圖失敗卡片照出
      } else if (form.preset) {
        await renamePtzPreset(cam.apiEndpoint, cam.homeId, cam.aid, form.preset.id, name, form.preset.version);
      }
      setForm(null);
      await reload();
    } catch (e) {
      setErr(errText(e, name));
    } finally {
      setBusy(false);
    }
  };

  const act = async (p: PtzPreset, what: 'update' | 'delete') => {
    const name = p.name ?? t('ptz.preset_n', { n: p.id });
    setMenu(null);
    if (what === 'delete' && !window.confirm(t('ptz.preset_delete_confirm', { name }))) return;
    setErr(null);
    try {
      if (what === 'update') {
        await updatePtzPresetPosition(cam.apiEndpoint, cam.homeId, cam.aid, p.id);
        const jpeg = await captureThumb?.().catch(() => null);
        if (jpeg) await putPtzPresetThumbnail(cam.apiEndpoint, cam.homeId, cam.aid, p.id, jpeg).catch(() => undefined);
      } else await deletePtzPreset(cam.apiEndpoint, cam.homeId, cam.aid, p.id);
      await reload();
    } catch (e) {
      setErr(errText(e, name));
    }
  };

  if (!list) return err ? <p className="error small">{err}</p> : null;
  const hereId = here.current.here;
  return (
    <div className="ptz-dpresets" data-testid="ptz-dpresets">
      <div className="ptz-dpresets-head">
        <strong role="heading" aria-level={3} aria-label={t('ptz.presets_count_a11y', { n: list.presets.length, max: list.max })}>
          {t('ptz.presets_count', { n: list.presets.length, max: list.max })}
        </strong>
        <span className="spacer" />
        <button
          type="button"
          className="btn btn-sm btn-text"
          disabled={list.full || list.deviceUnreachable}
          aria-disabled={list.full || undefined}
          onClick={() => setForm({ mode: 'add', name: t('ptz.preset_n', { n: presetDefaultNameNumber(list) }) })}
        >
          {list.full ? t('ptz.preset_full') : t('ptz.preset_add')}
        </button>
        {list.presets.length > 0 && (
          <button type="button" className="btn btn-sm btn-text" aria-label={t(manage ? 'ptz.preset_done_a11y' : 'ptz.preset_manage_a11y')} aria-pressed={manage} onClick={() => setManage((m) => !m)}>
            {t(manage ? 'ptz.preset_done' : 'ptz.preset_manage')}
          </button>
        )}
      </div>
      {list.deviceUnreachable && <p className="small muted">{t('ptz.preset_unreachable')}</p>}
      {list.presets.length > 0 && (
        <div className="ptz-preset-row">
          {list.presets.map((p) => (
            <PresetCard key={p.id} cam={cam} p={p} here={hereId === p.id} going={going === p.id} manage={manage} onGoto={() => void goto(p)} onMenu={() => setMenu(p)} />
          ))}
        </div>
      )}
      {menu && (
        <div className="ot-menu ptz-preset-menu" role="menu" aria-label={t('ptz.preset_menu')}>
          <button type="button" role="menuitem" onClick={() => (setMenu(null), setForm({ mode: 'rename', preset: menu, name: menu.name ?? t('ptz.preset_n', { n: menu.id }) }))}>
            {t(menu.named ? 'ptz.preset_rename' : 'ptz.preset_name_it')}
          </button>
          <button type="button" role="menuitem" disabled={list.deviceUnreachable} onClick={() => void act(menu, 'update')}>
            {t('ptz.preset_update')}
          </button>
          <button type="button" role="menuitem" disabled={list.deviceUnreachable} onClick={() => void act(menu, 'delete')}>
            {t('ptz.preset_delete')}
          </button>
          <button type="button" role="menuitem" onClick={() => setMenu(null)}>
            {t('common.cancel')}
          </button>
        </div>
      )}
      {form && (
        <form
          className="ptz-preset-form"
          onSubmit={(e) => {
            e.preventDefault();
            void save();
          }}
        >
          <label className="small">
            {t('ptz.preset_name_label')}
            <input className="input" autoFocus maxLength={64} value={form.name} onFocus={(e) => e.currentTarget.select()} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-describedby="ptz-preset-hint" />
          </label>
          <span id="ptz-preset-hint" className="small muted">
            {t('ptz.preset_name_hint')}
          </span>
          <div className="ptz-preset-form-actions">
            <button type="button" className="btn btn-sm" onClick={() => setForm(null)}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
              {t('ptz.preset_save')}
            </button>
          </div>
        </form>
      )}
      {err && (
        <p className="error small" role="alert">
          {err}
        </p>
      )}
      {!err && note && (
        <p className="small muted" role="status">
          {note}
        </p>
      )}
    </div>
  );
}
